import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import * as React from 'react';
import { portalExpansionApi } from '../lib/portal-expansion-api';
import { useToast, getErrorMessage } from '../hooks/use-toast';
import { ToastContainer } from '../components/toast';

export default function OnboardingReviewPage() {
  const [page, setPage] = useState(1);
  const [selectedMember, setSelectedMember] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { toasts, showToast, removeToast } = useToast();

  // Onboarding review uses pending dependents as the actual queue
  const { data, isLoading, error } = useQuery({
    queryKey: ['pending-dependents', page],
    queryFn: () => portalExpansionApi.getPendingDependents(page, 20),
    retry: 1,
  });

  const verifyMutation = useMutation({
    mutationFn: async ({ relationshipId, decision, notes }: { relationshipId: string; decision: 'full' | 'limited' | 'denied'; notes?: string }) => {
      return portalExpansionApi.verifyDependent(relationshipId, decision, notes);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pending-dependents'] });
      setSelectedMember(null);
      showToast('Dependent verification updated', 'success');
    },
    onError: (error: any) => {
      showToast(getErrorMessage(error), 'error');
    },
  });

  if (error) {
    return (
      <div className="error-state">
        <h2>Unable to load onboarding queue</h2>
        <p>
          {(error as any)?.response?.status === 404
            ? 'This endpoint is not yet implemented in the backend.'
            : 'An error occurred loading the onboarding review queue.'}
        </p>
        <button onClick={() => window.location.reload()} className="btn">
          Retry
        </button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="loading-state">
        <div className="spinner" />
        <p>Loading onboarding queue...</p>
      </div>
    );
  }

  const members = data?.dependents || [];
  const total = data?.total || 0;

  return (
    <>
      <ToastContainer toasts={toasts} removeToast={removeToast} />
      <div className="pagehead">
        <div>
          <h1>Onboarding Review</h1>
          <p>Review member identity verification and onboarding progress</p>
        </div>
      </div>

      {members.length === 0 ? (
        <div className="empty-state">
          <svg
            className="icon-large"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.65}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
          <h3>All caught up</h3>
          <p>There are no members pending onboarding review.</p>
        </div>
      ) : (
        <>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Member</th>
                  <th>Treaty #</th>
                  <th>Personal Info</th>
                  <th>Card Images</th>
                  <th>Dependents</th>
                  <th>Banking</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {members.map((dependent: any) => (
                  <tr key={dependent.id}>
                    <td>
                      <div className="member-cell">
                        <strong>
                          {dependent.legalFirstName} {dependent.legalLastName}
                        </strong>
                        <small>Person ID: {dependent.personId.slice(0, 8)}...</small>
                      </div>
                    </td>
                    <td>{dependent.treatyNumber || '—'}</td>
                    <td>
                      <StatusBadge status={dependent.legalFirstName ? 'complete' : 'pending'} />
                    </td>
                    <td>
                      <StatusBadge status="pending" />
                    </td>
                    <td>
                      <StatusBadge status={dependent.authorityLevel} />
                    </td>
                    <td>—</td>
                    <td>
                      <StatusBadge status={dependent.authorityLevel} />
                    </td>
                    <td>
                      <button
                        onClick={() => setSelectedMember(dependent.id)}
                        className="btn-small"
                      >
                        Review
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {total > 20 && (
            <div className="pagination">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="btn-secondary"
              >
                Previous
              </button>
              <span>
                Page {page} of {Math.ceil(total / 20)}
              </span>
              <button
                onClick={() => setPage((p) => p + 1)}
                disabled={page >= Math.ceil(total / 20)}
                className="btn-secondary"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {selectedMember && (
        <ReviewModal
          relationshipId={selectedMember}
          onClose={() => setSelectedMember(null)}
          onVerify={(decision, notes) =>
            verifyMutation.mutate({ relationshipId: selectedMember, decision, notes })
          }
          isSubmitting={verifyMutation.isPending}
        />
      )}

      <style>{`
        .pagehead {
          margin-bottom: 25px;
        }

        .pagehead h1 {
          font-size: 30px;
          font-weight: 600;
          letter-spacing: -0.85px;
          color: #313739;
        }

        .pagehead p {
          font-size: 13px;
          margin-top: 6px;
          color: #68716d;
        }

        .error-state,
        .loading-state,
        .empty-state {
          text-align: center;
          padding: 60px 20px;
        }

        .spinner {
          width: 40px;
          height: 40px;
          border: 4px solid #f2f3ee;
          border-top-color: #4b607f;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
          margin: 0 auto 20px;
        }

        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }

        .error-state h2 {
          font-size: 18px;
          margin-bottom: 10px;
          color: #d14343;
        }

        .error-state p,
        .loading-state p {
          font-size: 13px;
          color: #68716d;
          margin-bottom: 20px;
        }

        .icon-large {
          width: 48px;
          height: 48px;
          color: #68716d;
          margin: 0 auto 16px;
        }

        .empty-state h3 {
          font-size: 16px;
          font-weight: 600;
          color: #68716d;
          margin-bottom: 8px;
        }

        .empty-state p {
          font-size: 13px;
          color: #68716d;
        }

        .table-container {
          background: white;
          border-radius: 12px;
          padding: 20px;
          margin-bottom: 20px;
          overflow-x: auto;
        }

        .data-table {
          width: 100%;
          border-collapse: collapse;
        }

        .data-table th {
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: #68716d;
          text-align: left;
          padding: 12px 12px;
          border-bottom: 2px solid #f2f3ee;
        }

        .data-table td {
          padding: 16px 12px;
          border-bottom: 1px solid #f2f3ee;
          font-size: 13px;
          color: #424a47;
        }

        .data-table tbody tr:hover {
          background: #fafbf8;
        }

        .member-cell strong {
          display: block;
          font-size: 13px;
          font-weight: 500;
          color: #313739;
        }

        .member-cell small {
          display: block;
          font-size: 11px;
          color: #68716d;
          margin-top: 2px;
        }

        .btn,
        .btn-small,
        .btn-secondary {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 8px 16px;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 500;
          border: 1px solid #e6e9de;
          background: white;
          color: #424a47;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn:hover,
        .btn-small:hover,
        .btn-secondary:hover {
          background: #f6f6f3;
        }

        .btn-small {
          padding: 6px 12px;
          font-size: 12px;
        }

        .pagination {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 16px;
          padding: 20px;
        }

        .pagination button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        @media (max-width: 768px) {
          .table-container {
            padding: 12px;
          }

          .data-table th,
          .data-table td {
            padding: 10px 8px;
            font-size: 12px;
          }

          .member-cell strong {
            font-size: 12px;
          }

          .member-cell small {
            font-size: 10px;
          }
        }
      `}</style>
    </>
  );
}

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    complete: '#10b981',
    full: '#10b981',
    limited: '#f59e0b',
    pending: '#68716d',
    pending_review: '#68716d',
    in_progress: '#3b82f6',
    denied: '#ef4444',
  };

  return (
    <span
      style={{
        display: 'inline-block',
        padding: '4px 8px',
        borderRadius: '6px',
        fontSize: '11px',
        fontWeight: 500,
        background: `${colors[status] || '#68716d'}15`,
        color: colors[status] || '#68716d',
      }}
    >
      {status.replace(/_/g, ' ')}
    </span>
  );
}

function ReviewModal({
  relationshipId,
  onClose,
  onVerify,
  isSubmitting,
}: {
  relationshipId: string;
  onClose: () => void;
  onVerify: (decision: 'full' | 'limited' | 'denied', notes?: string) => void;
  isSubmitting: boolean;
}) {
  const [notes, setNotes] = React.useState('');

  return (
    <>
      <div className="modal-overlay" onClick={onClose} />
      <div className="modal">
        <div className="modal-header">
          <h2>Review Dependent Relationship</h2>
          <button onClick={onClose} className="modal-close" aria-label="Close">
            ×
          </button>
        </div>
        <div className="modal-body">
          <div className="review-section">
            <h3>Relationship ID</h3>
            <p style={{ fontSize: '12px', color: '#68716d', fontFamily: 'monospace' }}>
              {relationshipId}
            </p>
          </div>
          <div className="review-section">
            <h3>Admin Notes</h3>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional notes about this verification decision"
              rows={3}
              disabled={isSubmitting}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '13px',
              }}
            />
          </div>
        </div>
        <div className="modal-footer">
          <button onClick={onClose} className="btn-secondary" disabled={isSubmitting}>
            Close
          </button>
          <button
            onClick={() => onVerify('denied', notes || undefined)}
            className="btn-danger"
            disabled={isSubmitting}
          >
            Deny
          </button>
          <button
            onClick={() => onVerify('limited', notes || undefined)}
            className="btn-warning"
            disabled={isSubmitting}
          >
            Limited Access
          </button>
          <button
            onClick={() => onVerify('full', notes || undefined)}
            className="btn-primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Approving...' : 'Full Access'}
          </button>
        </div>
      </div>

      <style>{`
        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.5);
          z-index: 100;
        }

        .modal {
          position: fixed;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          background: white;
          border-radius: 12px;
          width: 90%;
          max-width: 600px;
          max-height: 80vh;
          display: flex;
          flex-direction: column;
          z-index: 101;
        }

        .modal-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 20px 24px;
          border-bottom: 1px solid #f2f3ee;
        }

        .modal-header h2 {
          font-size: 18px;
          font-weight: 600;
          color: #313739;
        }

        .modal-close {
          font-size: 28px;
          line-height: 1;
          color: #68716d;
          background: none;
          border: 0;
          padding: 0;
          width: 32px;
          height: 32px;
          cursor: pointer;
        }

        .modal-body {
          padding: 24px;
          overflow-y: auto;
          flex: 1;
        }

        .review-section {
          margin-bottom: 24px;
        }

        .review-section h3 {
          font-size: 14px;
          font-weight: 600;
          color: #313739;
          margin-bottom: 12px;
        }

        .progress-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .modal-footer {
          display: flex;
          gap: 12px;
          padding: 20px 24px;
          border-top: 1px solid #f2f3ee;
          justify-content: flex-end;
        }

        .btn-primary {
          padding: 10px 20px;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 500;
          border: 0;
          background: #4b607f;
          color: white;
          cursor: pointer;
        }

        .btn-primary:hover:not(:disabled) {
          background: #3d4f68;
        }

        .btn-primary:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .btn-danger {
          padding: 10px 20px;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 500;
          border: 0;
          background: #ef4444;
          color: white;
          cursor: pointer;
        }

        .btn-danger:hover:not(:disabled) {
          background: #dc2626;
        }

        .btn-danger:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .btn-warning {
          padding: 10px 20px;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 500;
          border: 0;
          background: #f59e0b;
          color: white;
          cursor: pointer;
        }

        .btn-warning:hover:not(:disabled) {
          background: #d97706;
        }

        .btn-warning:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        @media (max-width: 640px) {
          .modal {
            width: 95%;
            max-height: 90vh;
          }

          .modal-header,
          .modal-body,
          .modal-footer {
            padding: 16px;
          }
        }
      `}</style>
    </>
  );
}

